import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CheckInputSectionAccessRight } from "../../../common/Input";
import { Input } from "../../../common/Input";
import { Button } from "../../../common/APICommon";

export const AddViewUpdateAccessTemplateModal = ({
  visible,
  onClose,
  onSave,
  modalName,
  actionType = '',
  data,
  setData,
}) => {
  const [failMessage, setFailMessage] = useState("");
  const [descriptionErrorState, setDescriptionErrorState] = useState(false);
  const [userTimeoutErrorState, setUserTimeoutErrorState] = useState(false);

  // Reset failMessage when modal opens
  useEffect(() => {
    if (!visible) return;
    setFailMessage("");
    setDescriptionErrorState(false);
    setUserTimeoutErrorState(false);
  }, [visible, actionType]);

  if (!visible) return null;

  // const handleAccessRightChange = (category, itemKey, value) => {
  //   setData(prev => {
  //     const updated = { ...prev };
  //     updated.accessRights[category] = updated.accessRights[category].map(item =>
  //       item.key === itemKey ? { ...item, value } : item
  //     );
  //     return updated;
  //   });
  // };

  const handleAccessRightChange = (category, itemKey, value) => {
    setData(prev => {
      const updated = { ...prev };
      updated.accessRights[category] = updated.accessRights[category].map(item => {
        if (item.key === itemKey) {
          // Set both main value and all sub-types to match the toggle
          return {
            ...item,
            value: value,
            accessType: item.accessType?.map(sub => ({ ...sub, value: value })) || []
          };
        }
        return item;
      });
      return updated;
    });
  };

  const handleAccessTypeChange = (category, itemKey, typeKey, value) => {
    setData(prev => {
      const updated = { ...prev };
      updated.accessRights[category] = updated.accessRights[category].map(item => {
        if (item.key === itemKey) {
          const updatedAccessType = item.accessType.map(sub =>
            sub.key === typeKey ? { ...sub, value } : sub
          );
          return { ...item, accessType: updatedAccessType };
        }
        return item;
      });
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    if (!data.description) {
      setFailMessage("Please fill up required field/s.");
      setDescriptionErrorState(true);
      return;
    }

    if (!data.userTimeOut) {
      setUserTimeoutErrorState(true);
      return;
    }

    await onSave(e);
    setDescriptionErrorState(false);
    onClose();
  };

  const checkAll = () => {
    setData(prev => {
      const updated = { ...prev };
      Object.keys(updated.accessRights).forEach(section => {
        updated.accessRights[section] = updated.accessRights[section].map(item => ({
          ...item,
          value: true,
          accessType: item.accessType?.map(sub => ({
            ...sub,
            value: true, 
          })) || [],
        }));
      });
      return updated;
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[5000] bg-black/50 flex items-center justify-center p-3 text-gray-700 overflow-auto">
        <div className="bg-gray-50 w-[80%] min-[940px]:w-[70%] rounded-lg shadow-xl h-auto max-h-[95vh] flex flex-col relative">
          
          {/* Header - Standardized */}
          <div className="flex justify-between items-center px-6 py-2 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-lg shadow-sm">
            <h2 className="text-xl font-semibold text-gray-800 flex items-center space-x-2">
              <span className="text-xl text-blue-600">{modalName}</span>
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 transition-colors p-1 bg-transparent border-none cursor-pointer"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Body - Standardized */}
          <div className="p-6 overflow-y-auto flex-grow space-y-4">
            
            {/* Top Toolbar */}
            <div className="flex justify-between items-center bg-white p-3 border border-gray-300 rounded-lg shadow-sm">
              <span className="text-sm text-gray-500 font-medium">Quick Actions:</span>
              <Button
                icon={<CheckCircleIcon fontSize="small" />}
                name="Check All Rights"
                className="bg-blue-500 hover:bg-blue-600 text-xs border-none cursor-pointer"
                onClick={checkAll}
              />
            </div>

            {/* Template Information Section */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-4">
              <div className="py-1 border-b border-gray-300 mb-4">
                <h3 className="font-medium text-gray-700">Template Details</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Description:"
                  placeholder="Enter template description"
                  value={data.description ?? ""}
                  isRequired={true}
                  error={descriptionErrorState && !data.description}
                  errorMessage="Please fill up this field"
                  onChange={e => setData(prev => ({ ...prev, description: e.target.value }))}
                />

                <Input
                  label="User Timeout (Minutes):"
                  placeholder="0"
                  type="number"
                  value={data.userTimeOut ?? ""}
                  isRequired={true}
                  error={userTimeoutErrorState && !data.userTimeOut}
                  errorMessage="Please fill up this field"
                  onChange={e => setData(prev => ({ ...prev, userTimeOut: e.target.value }))}
                />
              </div>
            </div>

            {/* Access Rights Mapping Section */}
            <div className="border border-gray-300 rounded-lg bg-white px-4 shadow-sm pb-4">
              <div className="py-1 border-b border-gray-300 mb-4">
                <h3 className="font-medium text-gray-700">Access Rights Mapping</h3>
              </div>
              
              <div className="space-y-6">
                {Object.entries(data.accessRights)
                  .filter(([category]) => category !== "DevMode")
                  .map(([category, items]) => (
                    <div key={category} className="bg-gray-50 rounded-md p-3 border border-gray-100 ">
                      <CheckInputSectionAccessRight
                        name={category}
                        data={items}
                        onChangeAccessRight={(itemKey, value) => handleAccessRightChange(category, itemKey, value)}
                        onChangeAccessType={(itemKey, typeKey, value) => handleAccessTypeChange(category, itemKey, typeKey, value)}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>

          {/* Footer - Standardized */}
          <div className="flex justify-end space-x-2 p-5 border-t border-gray-200 bg-white rounded-b-lg shadow-sm">
            <button
              className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors"
              onClick={onClose}
            >
              CANCEL
            </button>
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded border-none cursor-pointer transition-colors"
              onClick={(e) => handleSubmit(e)}
            >
              SAVE
            </button>
          </div>
        </div>
      </div>
    </>
  );
};